import { formatSydneyDateTime } from './utils/timezone';
import AssistModeBanner from './components/platform/AssistModeBanner';
import AssistDiagnosticsCard from './components/platform/AssistDiagnosticsCard';
import QAssistantWidget from './components/QAssistantWidget';
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { TenantProvider, useTenant } from './lib/tenant';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationBell } from './components/NotificationBell';
import ThemeToggle from './components/ui/ThemeToggle';
import { Sidebar } from './components/Sidebar';
import { registerServiceWorker } from './hooks/usePushNotifications';
import { Menu, Shield, Loader2 } from 'lucide-react';

import './App.css';

// Custom scrollbar styles are in App.css

// Register service worker on app load
if (typeof window !== 'undefined') {
  registerServiceWorker().catch(console.error);
}

// Pages
import ContractorOperationsPortal from './pages/ContractorOperationsPortal';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import IncidentsPage from './pages/IncidentsPage';
import ClientsPage from './pages/ClientsPage';
import SitesPage from './pages/SitesPage';
import ShiftsPage from './pages/ShiftsPage';
import PatrolsPage from './pages/PatrolsPage';
import UsersPage from './pages/UsersPage';
import PersonnelCompliancePage from './pages/PersonnelCompliancePage';
import AuditLogPage from './pages/AuditLogPage';
import RosterHubPage from './pages/RosterHubPage';
import SiteRosterPage from './pages/SiteRosterPage';
import ClientRosterPage from './pages/ClientRosterPage';
import OperationsRosterBoard from './pages/OperationsRosterBoard';
import AttendancePage from './pages/AttendancePage';
import ExecutivePage from './pages/ExecutivePage';
import ContractorPayrollPage from './pages/ContractorPayrollPage';
import PayrollPage from './pages/PayrollPage';
import SectorExecutionPage from './pages/SectorExecutionPage';
import RoutesPage from './pages/RoutesPage';
import MyRoutesPage from './pages/MyRoutesPage';
import DailyActivityLogPage from './pages/DailyActivityLogPage';
import PatrolRunPage from './pages/PatrolRunPage';
import MyTimesheetsPage from './pages/MyTimesheetsPage';
import TimesheetApprovalsPage from './pages/TimesheetApprovalsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ShiftAssurancePage from './pages/ShiftAssurancePage';
import OfficerDashboard from './pages/OfficerDashboard';
import OfficerMasterLicencesPage from './pages/OfficerMasterLicencesPage';
import ReportsPage from './pages/ReportsPage';
import ReportingGroupsPage from './pages/ReportingGroupsPage';
import AdminDiagnosticsPage from './pages/AdminDiagnosticsPage';
import QReviewConsole from './pages/QReviewConsole';
import ComplianceReportsPage from './pages/ComplianceReportsPage';
import RosterPageV2 from './pages/RosterPageV2';
import FeedManagementPage from './pages/FeedManagementPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import OperationsReviewPage from './pages/OperationsReviewPage';
import OfflineQueueIndicator from './components/OfflineQueueIndicator';
import PatrolPlansPage from './pages/PatrolPlansPage';
import PatrolExecutionPage from './pages/PatrolExecutionPage';
import LiveOperationsPage from './pages/live/LiveOperationsPage';
import CommsPage from './pages/CommsPage';

// Platform Admin Pages
import PlatformLayout from './pages/platform/PlatformLayout';
import PlatformLoginPage from './pages/platform/PlatformLoginPage';
import PlatformTenantsPage from './pages/platform/PlatformTenantsPage';
import PlatformTenantDetailPage from './pages/platform/PlatformTenantDetailPage';
import PlatformMetricsPage from './pages/platform/PlatformMetricsPage';
import PlatformAuditPage from './pages/platform/PlatformAuditPage';
import PlatformAlertsPage from './pages/platform/PlatformAlertsPage';
import AdhocJobsPage from './pages/AdhocJobsPage';
import AdhocResponseReportsPage from './pages/AdhocResponseReportsPage';
import DispatchAlertOverlay from './components/dispatch/DispatchAlertOverlay';
import AuditTimelinePage from "./pages/AuditTimelinePage";
import ContractorPayPage from "./pages/ContractorPayPage";
import MasterLicencesPage from "./pages/MasterLicencesPage";
import FormStudioPage from './pages/FormStudioPage';
import FormTemplatesPage from './pages/FormTemplatesPage';
import FormsRegisterPage from './pages/FormsRegisterPage';
import MyFormsPage from './pages/MyFormsPage';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Contractor administrators are external organisation administrators.
  // Only explicitly contractor-scoped application routes are permitted.
  if (user?.role === 'contractor_admin') {
    const allowedPaths = [
      '/contractor/dashboard',
      '/contractor/team',
      '/contractor/shifts',
      '/contractor/timesheets',
      '/contractor/compliance',
      '/contractor/invoices',
      '/contractor/onboarding',
      '/change-password',
    ];

    const currentPath = window.location.pathname;

    const isAllowed = allowedPaths.some(
      (path) =>
        currentPath === path ||
        currentPath.startsWith(`${path}/`)
    );

    if (!isAllowed) {
      return <Navigate to="/contractor/onboarding" replace />;
    }
  }

  return children;
};

// Platform Admin Route - only platform_admin, enforces must_change_password
const PlatformProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#07070a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const isPlatformAdmin = user.platform_role === 'platform_admin' || user.role === 'platform_admin';
  
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-[#07070a] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Platform Access Denied</h1>
          <p className="text-zinc-400 mb-4">Only platform administrators can access this area.</p>
          <Button onClick={() => window.location.href = '/dashboard'} className="bg-amber-600 hover:bg-amber-700">
            Return to App
          </Button>
        </div>
      </div>
    );
  }
  
  // Block access until password is changed
  if (user.must_change_password) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Dashboard Router - Shows Officer Dashboard for officers, My Shifts redirect, Executive for admins
const DashboardRouter = () => {
  const { user, isAdmin } = useAuth();
  const adminRoles = ['super_admin', 'admin', 'operations_admin', 'supervisor'];
  const isAdminUser = adminRoles.includes(user?.role);
  const isOfficer = user?.role === 'security_officer';
  
  // Contractor administrators use their dedicated contractor portal.
  if (user?.role === 'contractor_admin') {
    return <Navigate to="/contractor/dashboard" replace />;
  }

  // Officers should use OfficerDashboard
  if (isOfficer) {
    return <OfficerDashboard />;
  }
  
  return isAdminUser ? <DashboardPage /> : <OfficerDashboard />;
};


const ContractorOnboardingEntry = () => {
  const { user } = useAuth();

  // Contractor administrators use their own operations portal.
  // The onboarding administration workspace remains for BSMS admins.
  if (user?.role === 'contractor_admin') {
    return <Navigate to="/contractor/dashboard" replace />;
  }

  return <ContractorOnboardingPortal />;
};

const ContractorSafeDispatchOverlay = () => {
  const { user } = useAuth();

  if (user?.role === 'contractor_admin') {
    return null;
  }

  return <DispatchAlertOverlay />;
};


// My Shifts Router
// Supervisors can perform operational shifts while retaining management access.
const MyShiftsRouter = () => {
  const { user } = useAuth();

  const operationalRoles = [
    'security_officer',
    'supervisor',
  ];

  if (operationalRoles.includes(user?.role)) {
    return <OfficerDashboard />;
  }

  return <ShiftsPage />;
};

// Role-Protected Route - Blocks access based on role
const RoleProtectedRoute = ({ children, allowedRoles = [], blockedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }
  
  // Check if user role is explicitly blocked
  if (blockedRoles.includes(user?.role)) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-zinc-400 mb-4">You do not have permission to access this page.</p>
          <Button onClick={() => window.history.back()} className="bg-blue-600 hover:bg-blue-700">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  // Check if user role is in allowed roles (if specified)
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">403 - Access Denied</h1>
          <p className="text-zinc-400 mb-4">This page is restricted to authorized personnel only.</p>
          <Button onClick={() => window.history.back()} className="bg-blue-600 hover:bg-blue-700">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  return children;
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSydneyTime, setCurrentSydneyTime] = useState(new Date());

  useEffect(() => {
    const clock = setInterval(() => setCurrentSydneyTime(new Date()), 1000);
    return () => clearInterval(clock);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Sidebar Component */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-72 min-w-0">
        {/* Top Bar */}
        <header className="h-14 bg-[#0f0f12]/95 backdrop-blur-sm border-b border-zinc-800 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-50" data-testid="topbar-header">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="hidden sm:block">
              <p className="text-xs text-zinc-500 font-mono">
                {formatSydneyDateTime(currentSydneyTime, true)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <OfflineQueueIndicator />
            <ThemeToggle />
              <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

/**
 * TenantBoot — syncs TenantProvider state into AuthProvider's sessionStorage
 * so axios interceptors can include tenant headers.
 */
const TenantBoot = ({ children }) => {
  const { mode, slug } = useTenant();
  const { setTenantContext } = useAuth();
  
  useEffect(() => {
    if (mode) {
      setTenantContext(mode, slug);
    }
  }, [mode, slug, setTenantContext]);
  
  return children;
};

/**
 * ModeAwareLogin — Shows platform login or tenant login based on detected mode.
 */
const ModeAwareLogin = () => {
  const hostname = window.location.hostname.toLowerCase();
  const subdomain = hostname.split('.')[0];

  const isPlatform =
    hostname === "rapidxtech.com.au" ||
    hostname === "www.rapidxtech.com.au" ||
    hostname === "app.rapidxtech.com.au" ||
    subdomain === "app" ||
    subdomain === "www";

  if (isPlatform) {
    return <PlatformLoginPage />;
  }

  // Anything else = tenant (bsms, etc)
  return <LoginPage />;
};

/**
 * ModeAwareRoot — Redirects to correct root based on mode.
 */
const ModeAwareRoot = () => {
  const { mode, slug } = useTenant();
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  if (user?.role === 'contractor_admin') {
    return <Navigate to="/contractor/dashboard" replace />;
  }

  if (user?.tenant_slug) {
    return <Navigate to="/dashboard" replace />;
  }

  if (mode === 'platform') {
    return <Navigate to="/platform" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <TenantProvider>
          <AuthProvider>
            <TenantBoot>
              <AssistModeBanner />
              <AssistDiagnosticsCard />
              <QAssistantWidget />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<ModeAwareLogin />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/contractor-onboarding/invitation"
                element={<ContractorOnboardingInvitation />}
              />
              <Route
                path="/contractor-onboarding/evidence-replacement"
                element={<ContractorEvidenceReplacement />}
              />
                
                {/* Platform Admin routes - completely separate UI */}
                <Route path="/platform" element={
                  <PlatformProtectedRoute>
                    <PlatformLayout><PlatformTenantsPage /></PlatformLayout>
                  </PlatformProtectedRoute>
                } />
                <Route path="/platform/tenants/:tenantId" element={
                  <PlatformProtectedRoute>
                    <PlatformLayout><PlatformTenantDetailPage /></PlatformLayout>
                  </PlatformProtectedRoute>
                } />
                <Route path="/platform/metrics" element={
                  <PlatformProtectedRoute>
                    <PlatformLayout><PlatformMetricsPage /></PlatformLayout>
                  </PlatformProtectedRoute>
                } />
                <Route path="/platform/alerts" element={
                  <ProtectedRoute requirePlatform>
                    <PlatformLayout><PlatformAlertsPage /></PlatformLayout>
                  </ProtectedRoute>
                } />
                <Route path="/platform/audit" element={
                  <PlatformProtectedRoute>
                    <PlatformLayout><PlatformAuditPage /></PlatformLayout>
                  </PlatformProtectedRoute>
                } />

                {/* Root redirect — mode-aware */}
                <Route path="/" element={<ModeAwareRoot />} />

                {/* Tenant protected routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Routes>
                          <Route path="/dashboard" element={<DashboardRouter />} />
                          <Route path="/officer/master-licences" element={<OfficerMasterLicencesPage />} />

                          <Route path="/live-operations" element={
                            <RoleProtectedRoute blockedRoles={['security_officer']}>
                              <LiveOperationsPage />
                            </RoleProtectedRoute>
                          } />
                          
                          {/* Management-only routes - blocked for officers */}
                          <Route path="/executive" element={
                            <RoleProtectedRoute blockedRoles={['security_officer']}>
                              <ExecutivePage />
                            </RoleProtectedRoute>
                          } />
                      <Route path="/shift-assurance" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <ShiftAssurancePage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/attendance" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <AttendancePage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/roster" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <RosterHubPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/operations-roster-board/:siteId" element={
                        <ProtectedRoute>
                          <OperationsRosterBoard />
                        </ProtectedRoute>
                      } />

                      <Route path="/roster-sites" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <RosterHubPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/roster/site/:siteId" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <SiteRosterPage />
                        </RoleProtectedRoute>
                      } />

                      <Route path="/client/:clientId/roster" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <ClientRosterPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/timesheet-approvals" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <TimesheetApprovalsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/payroll" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <PayrollPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/contractor-pay" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <ContractorPayrollPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/clients" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <ClientsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/sites" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <SitesPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/reporting-groups" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                          <ReportingGroupsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/reports" element={
                        <RoleProtectedRoute>
                          <ReportsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/operations-review" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <OperationsReviewPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/compliance-reports" element={
                        <RoleProtectedRoute blockedRoles={['security_officer']}>
                          <ComplianceReportsPage />
                        </RoleProtectedRoute>
                      } />
          <Route path="/master-licences" element={<MasterLicencesPage />} />
                      <Route path="/personnel-compliance" element={<PersonnelCompliancePage />} />
                      <Route path="/users" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <UsersPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/audit" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <AuditLogPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/feed-management" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                          <FeedManagementPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/q-review" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                          <QReviewConsole />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/diagnostics" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin']}>
                          <AdminDiagnosticsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/patrol-plans" element={
                        <RoleProtectedRoute allowedRoles={['super_admin', 'admin', 'operations_admin']}>
                          <PatrolPlansPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/admin/user-management" element={<Navigate to="/users" replace />} />
                      <Route path="/change-password" element={<ChangePasswordPage />} />
                      
                      {/* RapidX Comms - operational communications for tenant users */}
                      <Route path="/comms" element={<CommsPage />} />

                      {/* Officer-accessible routes - available to all roles */}
                      <Route path="/my-shifts" element={<MyShiftsRouter />} />
                      <Route path="/my-timesheets" element={<MyTimesheetsPage />} />
                      <Route path="/adhoc-jobs" element={<AdhocJobsPage />} />
          <Route path="/incidents" element={<IncidentsPage />} />
                      <Route path="/shifts" element={<ShiftsPage />} />
                      <Route path="/patrols" element={<PatrolsPage />} />
                      <Route path="/my-routes" element={<MyRoutesPage />} />
                      <Route path="/daily-activity-log" element={<DailyActivityLogPage />} />
                      <Route path="/patrol-run/:runId" element={<PatrolRunPage />} />
                      <Route path="/patrol-execution/:shiftId" element={<PatrolExecutionPage />} />
                      <Route path="/sector-execution/:siteId" element={<SectorExecutionPage />} />
                      <Route path="/sector-execution/:siteId/:shiftId" element={<SectorExecutionPage />} />
                      
                      <Route path="/my-forms" element={<MyFormsPage />} />
                      <Route path="/forms-register" element={<FormsRegisterPage />} />
                      <Route path="/forms-studio" element={<FormTemplatesPage />} />
                      <Route path="/forms-studio/new" element={<FormStudioPage />} />
                      <Route path="/forms-studio/:id" element={<FormStudioPage />} />
                      
                      {/* Redirects */}
                      <Route path="/routes" element={<Navigate to="/my-routes" replace />} />
                              <Route path="/audit-timeline" element={<AuditTimelinePage />} />
                <Route path="/compliance/agreements/templates" element={<AgreementTemplateStudio />} />
              <Route path="/onboarding/my" element={<MyOnboarding />} />
              <Route path="/compliance/onboarding" element={<OnboardingAdmin />} />
              <Route
                path="/contractor/dashboard"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="dashboard"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/team"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="team"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/shifts"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="shifts"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/timesheets"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="timesheets"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/compliance"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="compliance"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/invoices"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin'
                    ]}
                  >
                    <ContractorOperationsPortal
                      view="invoices"
                    />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/contractor/onboarding"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'contractor_admin',
                      'super_admin',
                      'admin',
                      'admin_manager',
                      'operations_admin'
                    ]}
                  >
                    <ContractorOnboardingPortal />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/contractor-onboarding"
                element={
                  <RoleProtectedRoute
                    allowedRoles={[
                      'super_admin',
                      'admin_manager'
                    ]}
                  >
                    <ContractorOnboardingAdmin />
                  </RoleProtectedRoute>
                }
              />
      </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
</Routes>
          <ContractorSafeDispatchOverlay />
          <Toaster />
          </TenantBoot>
        </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
import AgreementTemplateStudio from "./pages/AgreementTemplateStudio";
import MyOnboarding from "./pages/MyOnboarding";
import OnboardingAdmin from "./pages/OnboardingAdmin";
import ContractorEvidenceReplacement from "./pages/ContractorEvidenceReplacement";
import ContractorOnboardingAdmin from "./pages/ContractorOnboardingAdmin";
import ContractorOnboardingInvitation from "./pages/ContractorOnboardingInvitation";
import ContractorOnboardingPortal from "./pages/ContractorOnboardingPortal";

export default App;
